import { type LoginInput, loginSchema } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { apiErrorMessage } from '../../../lib/api-error-message';
import { login } from '../api';
import { useAuthStore } from '../store';

export interface DemoLoginHint {
  alias: string;
  password: string;
}

interface Props {
  /** Mode démo détecté : affiche l’admin recréé après chaque reset (> phase 0). */
  demoHint?: DemoLoginHint | null;
}

export function LoginForm({ demoHint }: Props) {
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { alias: '', password: '' },
  });

  return (
    <form
      className="app-card mx-auto w-full max-w-md space-y-6 p-7 sm:p-9"
      noValidate
      onSubmit={handleSubmit(async (input) => {
        setError(null);
        try {
          const res = await login(input);
          setSession(res.accessToken, res.user);
        } catch (err) {
          setError(apiErrorMessage(err));
        }
      })}
    >
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-900 lg:text-[1.375rem]">
          Connexion
        </h2>
        <p className="mt-2 text-[0.9rem] leading-relaxed text-slate-600">
          Identifiez-vous avec votre alias. Si vous n’en avez pas, demandez à votre administrateur
          de créer votre compte.
        </p>
        {demoHint && (
          <div
            role="note"
            className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/95 px-4 py-3 text-[0.85rem] leading-relaxed text-emerald-950"
          >
            <p className="font-semibold text-emerald-900">
              Reset démo : un nouvel administrateur est recréé (l’ancien compte et ses sessions sont
              supprimés).
            </p>
            <p className="mt-2">
              Alias :{' '}
              <code className="rounded bg-emerald-100/90 px-1.5 py-0.5 font-mono text-[0.82rem]">
                {demoHint.alias}
              </code>
            </p>
            <p className="mt-1">
              Mot de passe :{' '}
              <code className="rounded bg-emerald-100/90 px-1.5 py-0.5 font-mono text-[0.82rem]">
                {demoHint.password}
              </code>
            </p>
            <div className="mt-3 flex">
              <button
                type="button"
                className="rounded-lg border border-emerald-400/80 bg-white/90 px-3 py-1.5 text-[0.78rem] font-semibold tracking-tight text-emerald-950 shadow-sm hover:bg-emerald-100/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
                onClick={() => {
                  setError(null);
                  setValue('alias', demoHint.alias, { shouldValidate: true, shouldDirty: true });
                  setValue('password', demoHint.password, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              >
                Remplir
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-5">
        <div className="block">
          <label htmlFor="alias" className="app-label">
            Alias
          </label>
          <input
            id="alias"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck="false"
            className="app-input"
            {...register('alias')}
          />
          {errors.alias && <p className="text-error mt-2">{errors.alias.message}</p>}
        </div>

        <div className="block">
          <label htmlFor="password" className="app-label">
            Mot de passe
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className="app-input"
            {...register('password')}
          />
          {errors.password && <p className="text-error mt-2">{errors.password.message}</p>}
        </div>
      </div>

      {error && (
        <p className="text-error rounded-xl border border-red-200 bg-red-50 px-4 py-3">{error}</p>
      )}

      <button
        type="submit"
        className="btn-primary w-full shadow-xl shadow-teal-800/35"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Connexion…' : 'Se connecter'}
      </button>
    </form>
  );
}
